import ListPagination from 'Components/ListPagination/ListPagination'
import { useListPagination } from 'Hooks/useListPagination'
import { useEffect, useMemo, useState, Key } from 'react'
import { Link } from 'react-router-dom'
import './Referrals.scss'
import {
  BnpmPromoConfigXModel,
  BnpmPromoWinXModel,
  SortOrder,
} from 'BoostXpressAdmin/Model/bnpmPromoXModel'
import { ReferalXModel, ReferralSummaryXModel } from 'BoostXpressAdmin/Model/referalXModel'
import { bnpmPromoBXService } from 'BoostXpressAdmin/Services/bnpmPromoBXService'
import { referralBXService } from 'BoostXpressAdmin/Services/referralBXService'
import { HorizontalLoader, ModalWrapper } from 'Components'
import { handleError, handleSuccess } from 'Helpers'
import { TimeConverter } from 'Helpers/TimeConverter'
import { formatString } from 'Helpers/formatString'
import { SubmitHandler, useForm } from 'react-hook-form'
import { ToastContainer } from 'react-toastify'
import { DateTimeFormatter } from 'Helpers/DateTimeFormatter'
// import { stat } from 'fs'

type ReferralTab = 'referrals' | 'bnpm'

interface BnpmConfigState {
  isActive: boolean
  winProbability: number
  winPercentage: number
  firstWinCapMajor: number
  subsequentWinCapMajor: number
  maxWinsInWindow: number
  windowSize: number
  campaignBudgetMajor: number
  endsAt: string
  updatedByAdminEmail: string
  updatedAt: string | null
  remainingBudgetMajor: number
  referrerCutPercentage: number
}

interface ReferralConfigState {
  referrerBonusPercentage: number
  welcomeBonusPercentage: number
  winPercentage: number
  maxBonusMajor: number
  isActive: boolean
  updatedByAdminEmail: string
  updatedAt: string | null
}

interface BnpmConfigFormValues {
  isActive: boolean
  winProbability: number
  winPercentage: number
  firstWinCapMajor: number
  subsequentWinCapMajor: number
  maxWinsInWindow: number
  windowSize: number
  campaignBudgetMajor: number
  endsAt: string
  referrerCutPercentage: number
}

const defaultBnpmConfig: BnpmConfigState = {
  isActive: false,
  winProbability: 0,
  winPercentage: 0,
  firstWinCapMajor: 0,
  subsequentWinCapMajor: 0,
  maxWinsInWindow: 0,
  windowSize: 0,
  campaignBudgetMajor: 0,
  endsAt: new Date().toISOString(),
  updatedByAdminEmail: '',
  updatedAt: null,
  remainingBudgetMajor: 0,
  referrerCutPercentage: 0,
}

const toDateTimeLocalValue = (iso: string) => {
  if (!iso) return ''
  const date = new Date(iso)
  if (Number.isNaN(date.getTime())) return ''
  const pad = (value: number) => String(value).padStart(2, '0')
  const year = date.getFullYear()
  const month = pad(date.getMonth() + 1)
  const day = pad(date.getDate())
  const hours = pad(date.getHours())
  const minutes = pad(date.getMinutes())
  return `${year}-${month}-${day}T${hours}:${minutes}`
}

const fromDateTimeLocalValue = (value: string) => {
  if (!value) return ''
  const date = new Date(value)
  if (Number.isNaN(date.getTime())) return ''
  return date.toISOString()
}

// const deriveCycleCount = (referral: ReferalXModel) => {
//   const rawValue = Math.round(
//     referral.totalRewardPercentage || referral.referrerSharePercentage || 0,
//   )
//   return rawValue > 0 ? rawValue : 1
// }

// const deriveTransactionCount = (referral: ReferalXModel) => {
//   return referral.status === 'ORDER_PLACED' ? 1 : 0
// }

const formatCurrency = (amount: number) => `₦${amount.toLocaleString()}`

const buildBnpmConfigState = (
  apiConfig?: Partial<BnpmPromoConfigXModel> | null,
): BnpmConfigState => ({
  ...defaultBnpmConfig,
  isActive: apiConfig?.isActive ?? defaultBnpmConfig.isActive,
  winProbability: apiConfig?.winProbability ?? defaultBnpmConfig.winProbability,
  firstWinCapMajor: apiConfig?.firstWinCapMajor ?? defaultBnpmConfig.firstWinCapMajor,
  remainingBudgetMajor: apiConfig?.remainingBudgetMajor ?? defaultBnpmConfig.remainingBudgetMajor,
  winPercentage: apiConfig?.winPercentage ?? defaultBnpmConfig.winPercentage,
  subsequentWinCapMajor:
    apiConfig?.subsequentWinCapMajor ?? defaultBnpmConfig.subsequentWinCapMajor,
  maxWinsInWindow: apiConfig?.maxWinsInWindow ?? defaultBnpmConfig.maxWinsInWindow,
  windowSize: apiConfig?.windowSize ?? defaultBnpmConfig.windowSize,
  campaignBudgetMajor: apiConfig?.campaignBudgetMajor ?? defaultBnpmConfig.campaignBudgetMajor,
  endsAt: apiConfig?.endsAt ?? defaultBnpmConfig.endsAt,
  referrerCutPercentage:
    apiConfig?.referrerCutPercentage ?? defaultBnpmConfig.referrerCutPercentage,
  updatedByAdminEmail: apiConfig?.updatedByAdminEmail ?? defaultBnpmConfig.updatedByAdminEmail,
  updatedAt: apiConfig?.updatedAt ?? defaultBnpmConfig.updatedAt,
})

const buildBnpmFormValues = (config: BnpmConfigState): BnpmConfigFormValues => ({
  isActive: config.isActive,
  winProbability: Number(config.winProbability ?? 0) * 100,
  winPercentage: Number(config.winPercentage ?? 0) * 100,
  firstWinCapMajor: Number(config.firstWinCapMajor ?? 0),
  subsequentWinCapMajor: Number(config.subsequentWinCapMajor ?? 0),
  maxWinsInWindow: Number(config.maxWinsInWindow ?? 0),
  windowSize: Number(config.windowSize ?? 0),
  campaignBudgetMajor: Number(config.campaignBudgetMajor ?? 0),
  endsAt: toDateTimeLocalValue(config.endsAt),
  referrerCutPercentage: Number(config.referrerCutPercentage ?? 0),
})

const Referrals = () => {
  const [activeTab, setActiveTab] = useState<ReferralTab>('referrals')
  const [searchTerm, setSearchTerm] = useState('')
  const {
    page: currentPage,
    setPage: setCurrentPage,
    limit: referralLimit,
    changePageSize: changeReferralPageSize,
  } = useListPagination()

  const [bnpmSearchUserResourceId, setBnpmSearchUserResourceId] = useState('')
  const [bnpmStartDate, setBnpmStartDate] = useState('')
  const [bnpmEndDate, setBnpmEndDate] = useState('')
  const [bnpmSortOrder, setBnpmSortOrder] = useState<SortOrder>('DESC')
  const {
    page: bnpmPage,
    setPage: setBnpmPage,
    limit: bnpmLimit,
    changePageSize: changeBnpmPageSize,
  } = useListPagination()
  const [bnpmWins, setBnpmWins] = useState<BnpmPromoWinXModel[]>([])
  const [bnpmWinsLoading, setBnpmWinsLoading] = useState(false)
  const [bnpmWinsTotal, setBnpmWinsTotal] = useState(0)

  const [stats, setStats] = useState<ReferralSummaryXModel>({
    // totalReferrals: 0,
    // totalSuccessful: 0,
    // totalPending: 0,
    // totalBonusPaidMajor: 0,

    transactionTotalsSummary: {
      referralBonusPaidMajor: 0,
      welcomeBonusPaidMajor: 0,
      bnpmPromoRefundPaidMajor: 0,
    },
    referralsSummary: {
      totalReferrals: 0,
      totalSuccessful: 0,
      totalPending: 0,
    },
  })
  const [statsLoading, setStatsLoading] = useState(false)

  const [allReferrals, setAllReferrals] = useState<ReferalXModel[]>([])
  const [isLoading, setIsLoading] = useState(false)
  const [isExporting, setIsExporting] = useState(false)
  const [total, setTotal] = useState(0)

  const [showReferralFilters, setShowReferralFilters] = useState(false)
  const [status, setStatus] = useState('')
  const [referrerResourceId, setReferrerResourceId] = useState('')
  const [startDate, setStartDate] = useState('')
  const [endDate, setEndDate] = useState('')

  const handleExport = async () => {
    try {
      setIsExporting(true)
      const params: any = {
        sortOrder: 'DESC',
      }

      if (status) params.status = status
      if (referrerResourceId) params.referrerResourceId = referrerResourceId
      if (startDate) params['startDateYYYY_MM_DD'] = startDate
      if (endDate) params['endDateYYYY_MM_DD'] = endDate
      if (/^\d+$/.test(searchTerm.trim())) params.referrerResourceId = searchTerm.trim()

      const res = await referralBXService.exportCSV(params)
      const url = window.URL.createObjectURL(new Blob([res.data]))
      const link = document.createElement('a')
      link.href = url
      link.setAttribute('download', `referrals-${new Date().getTime()}.csv`)
      document.body.appendChild(link)
      link.click()
      link.remove()
    } catch (error) {
      console.error('Referral export failed:', error)
    } finally {
      setIsExporting(false)
    }
  }

  const [showBnpmFilters, setShowBnpmFilters] = useState(false)

  const [isReferralConfigModalOpen, setIsReferralConfigModalOpen] = useState(false)
  const [isReferralConfigSubmitting, setIsReferralConfigSubmitting] = useState(false)
  const [referralConfig, setReferralConfig] = useState<ReferralConfigState | null>(null)
  const [referralConfigLoading, setReferralConfigLoading] = useState(false)

  const [isConfigModalOpen, setIsConfigModalOpen] = useState(false)
  const [isConfigSubmitting, setIsConfigSubmitting] = useState(false)
  const [bnpmConfigLoading, setBnpmConfigLoading] = useState(false)
  const [bnpmConfig, setBnpmConfig] = useState<BnpmConfigState>(defaultBnpmConfig)
  const { register, handleSubmit, reset } = useForm({
    defaultValues: {
      referrerBonusPercentage: 0,
      welcomeBonusPercentage: 0,
      maxBonusMajor: 0,
      isActive: true,
    },
  })
  const {
    register: registerBnpm,
    handleSubmit: handleSubmitBnpm,
    reset: resetBnpm,
    watch: watchBnpm,
    setValue: setBnpmValue,
    formState: { errors: bnpmErrors },
  } = useForm<BnpmConfigFormValues>({
    defaultValues: buildBnpmFormValues(defaultBnpmConfig),
  })

  useEffect(() => {
    let cancelled = false

    const loadReferrals = async () => {
      if (activeTab !== 'referrals') return
      try {
        setIsLoading(true)
        const params: any = {
          page: currentPage,
          limit: referralLimit,
          sortOrder: 'DESC',
        }

        if (status) params.status = status
        if (referrerResourceId) params.referrerResourceId = referrerResourceId
        if (startDate) params['startDateYYYY_MM_DD'] = startDate
        if (endDate) params['endDateYYYY_MM_DD'] = endDate
        if (/^\d+$/.test(searchTerm.trim())) params.referrerResourceId = searchTerm.trim()

        const res = await referralBXService.index(params)
        if (cancelled) return
        setAllReferrals(res.data.data ?? [])
        setTotal(res.data.meta?.pagination?.total ?? res.data.meta?.total ?? 0)
      } catch {
        if (!cancelled) {
          setAllReferrals([])
          setTotal(0)
        }
      } finally {
        if (!cancelled) setIsLoading(false)
      }
    }

    loadReferrals()

    return () => {
      cancelled = true
    }
  }, [
    activeTab,
    currentPage,
    referralLimit,
    searchTerm,
    status,
    referrerResourceId,
    startDate,
    endDate,
  ])

  useEffect(() => {
    let cancelled = false

    const loadBnpmWins = async () => {
      if (activeTab !== 'bnpm') return
      try {
        setBnpmWinsLoading(true)
        const params: any = {
          page: bnpmPage,
          limit: bnpmLimit,
          sortOrder: bnpmSortOrder,
        }

        if (bnpmSearchUserResourceId.trim()) params.userResourceId = bnpmSearchUserResourceId.trim()
        if (bnpmStartDate) params['startDateYYYY_MM_DD'] = bnpmStartDate
        if (bnpmEndDate) params['endDateYYYY_MM_DD'] = bnpmEndDate

        const res = await bnpmPromoBXService.indexWins(params)
        if (cancelled) return
        setBnpmWins(res.data.data ?? [])
        setBnpmWinsTotal(res.data.meta?.pagination?.total ?? res.data.meta?.total ?? 0)
      } catch (error) {
        if (!cancelled) {
          setBnpmWins([])
          setBnpmWinsTotal(0)
          handleError(error as any)
        }
      } finally {
        if (!cancelled) setBnpmWinsLoading(false)
      }
    }

    loadBnpmWins()

    return () => {
      cancelled = true
    }
  }, [
    activeTab,
    bnpmPage,
    bnpmLimit,
    bnpmSortOrder,
    bnpmSearchUserResourceId,
    bnpmStartDate,
    bnpmEndDate,
  ])

  useEffect(() => {
    let cancelled = false

    const loadStats = async () => {
      try {
        setStatsLoading(true)
        const res = await referralBXService.summary()
        if (cancelled) return
        setStats(res.data.data)
      } catch {
        if (!cancelled)
          setStats({
            transactionTotalsSummary: {
              referralBonusPaidMajor: 0,
              welcomeBonusPaidMajor: 0,
              bnpmPromoRefundPaidMajor: 0,
            },
            referralsSummary: {
              totalReferrals: 0,
              totalSuccessful: 0,
              totalPending: 0,
            },
          })
      } finally {
        if (!cancelled) setStatsLoading(false)
      }
    }

    loadStats()

    return () => {
      cancelled = true
    }
  }, [])

  // useEffect(() => {
  //   console.log({ referralBXService55: stats })
  // }, [stats])

  useEffect(() => {
    let cancelled = false

    const fetchReferralConfig = async () => {
      try {
        setReferralConfigLoading(true)
        const response = await referralBXService.getConfig()
        if (cancelled) return
        setReferralConfig(response.data.data ?? null)
        reset({
          referrerBonusPercentage: response.data.data?.referrerBonusPercentage ?? 0,
          welcomeBonusPercentage: response.data.data?.welcomeBonusPercentage ?? 0,
          maxBonusMajor: response.data.data?.maxBonusMajor ?? 0,
          isActive: response.data.data?.isActive ?? false,
        })
      } catch (error) {
        if (cancelled) return
        setReferralConfig(null)
        handleError(error as any)
      } finally {
        if (!cancelled) setReferralConfigLoading(false)
      }
    }

    fetchReferralConfig()

    return () => {
      cancelled = true
    }
  }, [])

  useEffect(() => {
    if (activeTab === 'referrals') setCurrentPage(1)
  }, [activeTab, status, referrerResourceId, startDate, endDate])

  useEffect(() => {
    if (activeTab === 'bnpm') setBnpmPage(1)
  }, [activeTab, bnpmSearchUserResourceId, bnpmStartDate, bnpmEndDate, bnpmSortOrder])

  useEffect(() => {
    let cancelled = false

    const fetchBnpmConfig = async () => {
      try {
        setBnpmConfigLoading(true)
        const response = await bnpmPromoBXService.getConfig()
        if (cancelled) return
        const mapped = buildBnpmConfigState(response.data.data ?? null)
        setBnpmConfig(mapped)
        resetBnpm(buildBnpmFormValues(mapped))
      } catch (error) {
        if (cancelled) return
        setBnpmConfig(defaultBnpmConfig)
        resetBnpm(buildBnpmFormValues(defaultBnpmConfig))
        handleError(error as any)
      } finally {
        if (!cancelled) setBnpmConfigLoading(false)
      }
    }

    fetchBnpmConfig()

    return () => {
      cancelled = true
    }
  }, [])

  // const filteredReferrals = useMemo(() => {
  //   const query = searchTerm.trim().toLowerCase()
  //   if (!query) return allReferrals

  //   return allReferrals.filter((referral) => {
  //     const haystack = [
  //       `${referral.referrer.firstName} ${referral.referrer.lastName}`,
  //       referral.referrer.email,
  //       referral.referrer.resourceId,
  //       `${referral.referred.firstName} ${referral.referred.lastName}`,
  //       referral.referred.email,
  //       referral.referred.resourceId,
  //     ]
  //       .join(' ')
  //       .toLowerCase()

  //     return haystack.includes(query)
  //   })
  // }, [allReferrals, searchTerm])

  // const paginationNumbers = useMemo(
  //   () => buildPaginationNumbers(currentPage, totalPages),
  //   [currentPage, totalPages],
  // )

  const bnpmCards = useMemo(
    () => [
      {
        label: 'Win Probability',
        value: `${bnpmConfig.winProbability * 100}%`,
        tone: 'blue',
      },
      {
        label: 'Probability window',
        value: `${bnpmConfig.windowSize}`,
        tone: 'green',
      },
      {
        label: 'Win Percentage',
        value: `${bnpmConfig.winPercentage * 100}%`,
        tone: 'green',
      },

      {
        label: 'Campaign Budget',
        value: formatCurrency(bnpmConfig.campaignBudgetMajor),
        tone: 'purple',
      },
      {
        label: 'BNPM Amount Paid',
        value: formatCurrency(stats.transactionTotalsSummary?.bnpmPromoRefundPaidMajor ?? 0),
        tone: 'orange',
      },
      {
        label: 'Remaining Budget',
        value: formatCurrency(bnpmConfig.remainingBudgetMajor),
        tone: 'orange',
      },

      {
        label: 'Max wins in Window',
        value: `${bnpmConfig.maxWinsInWindow}`,
        tone: 'orange',
      },
      {
        label: 'Referrer Cut Percentage',
        value: `${bnpmConfig.referrerCutPercentage * 100}%`,
        tone: 'orange',
      },
    ],
    [bnpmConfig, stats],
  )

  const handleReferralConfigSubmit = async (data: {
    referrerBonusPercentage: number
    welcomeBonusPercentage: number
    maxBonusMajor: number
    isActive: boolean
  }) => {
    setIsReferralConfigSubmitting(true)
    try {
      await referralBXService.updateConfig({
        referrerBonusPercentage: data.referrerBonusPercentage,
        welcomeBonusPercentage: data.welcomeBonusPercentage,
        maxBonusMajor: data.maxBonusMajor,
        isActive: data.isActive,
      })
      handleSuccess('Referral configuration updated successfully')
      setIsReferralConfigModalOpen(false)

      const response = await referralBXService.getConfig()
      setReferralConfig(response.data.data ?? null)
      reset({
        referrerBonusPercentage: response.data.data?.referrerBonusPercentage ?? 0,
        welcomeBonusPercentage: response.data.data?.welcomeBonusPercentage ?? 0,
        maxBonusMajor: response.data.data?.maxBonusMajor ?? 0,
        isActive: response.data.data?.isActive ?? false,
      })
    } catch (error) {
      handleError(error as any)
    } finally {
      setIsReferralConfigSubmitting(false)
    }
  }

  const openConfigModal = () => {
    resetBnpm(buildBnpmFormValues(bnpmConfig))
    setIsConfigSubmitting(false)
    setIsConfigModalOpen(true)
  }

  const closeConfigModal = () => {
    resetBnpm(buildBnpmFormValues(bnpmConfig))
    setIsConfigSubmitting(false)
    setIsConfigModalOpen(false)
  }

  const handleConfigSubmit: SubmitHandler<BnpmConfigFormValues> = async (data) => {
    setIsConfigSubmitting(true)

    try {
      await bnpmPromoBXService.updateConfig({
        isActive: data.isActive,
        winProbability: Number(data.winProbability) / 100 || 0,
        firstWinCapMajor: Number(data.firstWinCapMajor) || 0,
        winPercentage: Number(data.winPercentage) / 100 || 0,
        subsequentWinCapMajor: Number(data.subsequentWinCapMajor) || 0,
        maxWinsInWindow: Number(data.maxWinsInWindow) || 0,
        windowSize: Number(data.windowSize) || 0,
        campaignBudgetMajor: Number(data.campaignBudgetMajor) || 0,
        endsAt: fromDateTimeLocalValue(data.endsAt),
        referrerCutPercentage: Number(data.referrerCutPercentage) / 100 || 0,
      })

      const response = await bnpmPromoBXService.getConfig()
      const mapped = buildBnpmConfigState(response.data.data ?? null)
      setBnpmConfig(mapped)
      resetBnpm(buildBnpmFormValues(mapped))
      setIsConfigModalOpen(false)
      handleSuccess('BNPM configuration updated successfully.')
    } catch (error) {
      handleError(error as any)
    } finally {
      setIsConfigSubmitting(false)
    }
  }

  const clearReferralFilters = () => {
    setStatus('')
    setReferrerResourceId('')
    setStartDate('')
    setEndDate('')
    setSearchTerm('')
    setCurrentPage(1)
  }

  const clearBnpmFilters = () => {
    setBnpmSearchUserResourceId('')
    setBnpmStartDate('')
    setBnpmEndDate('')
    setBnpmSortOrder('DESC')
    setBnpmPage(1)
  }

  const renderBnpmTableBody = () => {
    if (bnpmWinsLoading) {
      return (
        <tr>
          <td colSpan={6} className='bx-referrals__table-cell text-center'>
            <HorizontalLoader />
          </td>
        </tr>
      )
    }

    if (bnpmWins.length === 0) {
      return (
        <tr>
          <td colSpan={7} className='bx-referrals__table-cell text-center py-4'>
            No BNPM wins found
          </td>
        </tr>
      )
    }

    return bnpmWins.map((win, index: number) => (
      <tr key={win.resourceId}>
        <td className='bx-referrals__table-cell'>{(bnpmPage - 1) * bnpmLimit + index + 1}</td>
        <td className='bx-referrals__table-cell'>
          {win.user?.firstName} {win.user?.lastName}
        </td>

        <td className='bx-referrals__table-cell'>{win.order?.referenceNumber ?? '-'}</td>
        <td className='bx-referrals__table-cell'>{formatString(win.winType)}</td>
        <td className='bx-referrals__table-cell'>{win.user?.email ?? '-'}</td>
        <td className='bx-referrals__table-cell'>{formatCurrency(win.amountMajor ?? 0)}</td>
        <td className='bx-referrals__table-cell'>{DateTimeFormatter(win.createdAt)}</td>
        <td className='bx-referrals__table-cell'>
          <Link
            className='bx-referrals__view-link'
            to={`/boostxpress/bnpm-referrals/${win.resourceId}`}
          >
            View
          </Link>
        </td>
      </tr>
    ))
  }

  const bnpmIsActive = watchBnpm('isActive')

  return (
    <div className='bx-referrals'>
      <div className='bx-referrals__header'>
        <h2 className='bx-referrals__title'>Customer Incentives</h2>
      </div>

      <div className='bx-referrals__panel'>
        <div className='bx-referrals__panel-top row g-3 align-items-center'>
          <div className='col-lg'>
            <div className='bx-referrals__tabs'>
              <button
                type='button'
                className={`bx-referrals__tab ${activeTab === 'referrals' ? 'bx-referrals__tab--active' : ''}`}
                onClick={() => setActiveTab('referrals')}
              >
                Referrals Management
              </button>
              <button
                type='button'
                className={`bx-referrals__tab ${activeTab === 'bnpm' ? 'bx-referrals__tab--active' : ''}`}
                onClick={() => setActiveTab('bnpm')}
              >
                BNPM Management
              </button>
            </div>
          </div>

          <div className='col-lg-auto'>
            {activeTab === 'bnpm' ? (
              <button type='button' className='bx-referrals__config-btn' onClick={openConfigModal}>
                BNPM Configuration
              </button>
            ) : (
              <button
                type='button'
                className='bx-referrals__config-btn'
                onClick={() => setIsReferralConfigModalOpen(true)}
              >
                Referral Configuration
              </button>
            )}
          </div>
        </div>

        {activeTab === 'referrals' ? (
          <div className='referrals-page'>
            <div className='stats-container'>
              {statsLoading ? (
                <div className='stat-card' style={{ borderLeftColor: '#3378f3' }}>
                  <div className='stat-content'>
                    <div className='stat-title'>Loading stats...</div>
                  </div>
                </div>
              ) : stats ? (
                <div className='row'>
                  <div className='col-md-3 mt-3'>
                    <div className='stat-card' style={{ borderLeftColor: '#3378f3' }}>
                      <div className='stat-content'>
                        <div className='stat-title'>Total Referrals</div>
                        <div className='stat-value'>{stats.referralsSummary.totalReferrals}</div>
                      </div>
                    </div>
                  </div>

                  <div className='col-md-3 mt-3'>
                    <div className='stat-card' style={{ borderLeftColor: '#2ecc71' }}>
                      <div className='stat-content'>
                        <div className='stat-title'>Active Referrals</div>
                        <div className='stat-value'>{stats.referralsSummary.totalSuccessful}</div>
                      </div>
                    </div>
                  </div>

                  <div className='col-md-3 mt-3'>
                    <div className='stat-card' style={{ borderLeftColor: '#863de5' }}>
                      <div className='stat-content'>
                        <div className='stat-title'>Pending Referrals</div>
                        <div className='stat-value'>{stats.referralsSummary.totalPending}</div>
                      </div>
                    </div>
                  </div>

                  <div className='col-md-3 mt-3'>
                    <div className='stat-card' style={{ borderLeftColor: '#f0973d' }}>
                      <div className='stat-content'>
                        <div className='stat-title'>Total Referral Bonus Paid</div>
                        <div className='stat-value'>
                          ₦{stats.transactionTotalsSummary.referralBonusPaidMajor.toLocaleString()}
                        </div>
                      </div>
                    </div>
                  </div>
                  <div className='col-md-3 mt-3'>
                    <div className='stat-card' style={{ borderLeftColor: '#f0973d' }}>
                      <div className='stat-content'>
                        <div className='stat-title'>Total Welcome Bonus Paid</div>
                        <div className='stat-value'>
                          ₦{stats.transactionTotalsSummary.welcomeBonusPaidMajor.toLocaleString()}
                        </div>
                      </div>
                    </div>
                  </div>

                  <div className='col-md-3 mt-3'>
                    <div className='stat-card' style={{ borderLeftColor: '#9333ea' }}>
                      <div className='stat-content'>
                        <div className='stat-title'>Referral Bonus Percentage</div>
                        <div className='stat-value'>
                          {referralConfigLoading
                            ? 'Loading...'
                            : `${referralConfig?.referrerBonusPercentage ?? 0}%`}
                        </div>
                      </div>
                    </div>
                  </div>

                  <div className='col-md-3 mt-3'>
                    <div className='stat-card' style={{ borderLeftColor: '#9333ea' }}>
                      <div className='stat-content'>
                        <div className='stat-title'>Welcome Bonus Percentage</div>
                        <div className='stat-value'>
                          {referralConfigLoading
                            ? 'Loading...'
                            : `${referralConfig?.welcomeBonusPercentage ?? 0}%`}
                        </div>
                      </div>
                    </div>
                  </div>

                  <div className='col-md-3 mt-3'>
                    <div className='stat-card' style={{ borderLeftColor: '#9333ea' }}>
                      <div className='stat-content'>
                        <div className='stat-title'>Maximum Bonus</div>
                        <div className='stat-value'>
                          {referralConfigLoading
                            ? 'Loading...'
                            : `₦${(referralConfig?.maxBonusMajor ?? 0).toLocaleString()}`}
                        </div>
                      </div>
                    </div>
                  </div>

                  <div className='col-md-3 mt-3'>
                    <div className='stat-card' style={{ borderLeftColor: '#9333ea' }}>
                      <div className='stat-content'>
                        <div className='stat-title'>Referral Status</div>
                        <div className='stat-value'>
                          <span
                            className={`config-value ${referralConfig?.isActive ? 'active' : 'inactive'}`}
                          >
                            {referralConfigLoading
                              ? 'Loading...'
                              : referralConfig?.isActive
                                ? 'Active'
                                : 'Inactive'}
                          </span>
                        </div>
                      </div>
                    </div>
                  </div>

                  <div className='col-md-3 mt-3'>
                    <div className='stat-card' style={{ borderLeftColor: '#9333ea' }}>
                      <div className='stat-content'>
                        <div className='stat-title'>Referral Config Last Update</div>
                        <div className='stat-value'>
                          <span className='config-value small'>
                            {referralConfigLoading
                              ? 'Loading...'
                              : referralConfig?.updatedAt
                                ? new Date(referralConfig.updatedAt).toLocaleDateString()
                                : '-'}
                          </span>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              ) : (
                <div className='stat-card' style={{ borderLeftColor: '#e74c3c' }}>
                  <div className='stat-content'>
                    <div className='stat-title'>Failed to load stats</div>
                  </div>
                </div>
              )}
            </div>

            <div className='referrals-content'>
              <div className='table-controls row'>
                <div className='col-12 col-md-8 col-lg-8 col-xxl-9'>
                  <div className='search-container'>
                    <input
                      type='text'
                      className='search-input'
                      placeholder='Search by USER ID'
                      value={searchTerm}
                      onChange={(e) => {
                        setSearchTerm(e.target.value)
                        setCurrentPage(1)
                      }}
                    />
                    <div className='search-icon'>
                      <svg
                        width='24'
                        height='24'
                        viewBox='0 0 24 24'
                        fill='none'
                        xmlns='http://www.w3.org/2000/svg'
                      >
                        <path
                          d='M21 21L16.5 16.5M19 11C19 15.4183 15.4183 19 11 19C6.58172 19 3 15.4183 3 11C3 6.58172 6.58172 3 11 3C15.4183 3 19 6.58172 19 11Z'
                          stroke='#868484'
                          strokeWidth='2'
                          strokeLinecap='round'
                          strokeLinejoin='round'
                        />
                      </svg>
                    </div>
                  </div>
                </div>

                <div className='col-6 col-md-4 col-lg-4 col-xxl-3'>
                  <div className='row'>
                    <div className='col-md-6 position-relative'>
                      <button
                        type='button'
                        className='bx-vouchers-actions__btn w-100'
                        onClick={() => setShowReferralFilters(true)}
                      >
                        <svg
                          width='20'
                          height='20'
                          viewBox='0 0 24 24'
                          fill='none'
                          xmlns='http://www.w3.org/2000/svg'
                        >
                          <path
                            d='M3 6H21M6 12H18M9 18H15'
                            stroke='#868484'
                            strokeWidth='2'
                            strokeLinecap='round'
                            strokeLinejoin='round'
                          />
                        </svg>
                        <span>Filters</span>
                      </button>

                      {showReferralFilters && (
                        <div className='bx-referrals-filters-dropdown'>
                          <div className='bx-filters-header'>
                            <h5>Filter Referrals</h5>
                            <button
                              type='button'
                              className='bx-filters-close'
                              onClick={() => setShowReferralFilters(false)}
                            >
                              ×
                            </button>
                          </div>

                          <div className='bx-filters-body'>
                            <div className='bx-filter-group'>
                              <label>Referral Status</label>
                              <select
                                value={status}
                                onChange={(e) => setStatus(e.target.value)}
                                className='form-control'
                              >
                                <option value=''>All Status</option>
                                <option value='SIGNED_UP'>Signed Up</option>
                                <option value='ORDER_PLACED'>Order Placed</option>
                              </select>
                            </div>

                            <div className='bx-filter-group'>
                              <label>Referrer User ID</label>
                              <input
                                type='text'
                                value={referrerResourceId}
                                onChange={(e) => setReferrerResourceId(e.target.value)}
                                className='form-control'
                                placeholder='Enter USER ID'
                              />
                            </div>

                            <div className='bx-filter-group'>
                              <label>Start Date</label>
                              <input
                                type='date'
                                value={startDate}
                                onChange={(e) => setStartDate(e.target.value)}
                                className='form-control'
                              />
                            </div>

                            <div className='bx-filter-group'>
                              <label>End Date</label>
                              <input
                                type='date'
                                value={endDate}
                                onChange={(e) => setEndDate(e.target.value)}
                                className='form-control'
                              />
                            </div>
                          </div>

                          <div className='bx-filters-footer'>
                            <button
                              type='button'
                              className='btn btn-secondary'
                              onClick={clearReferralFilters}
                            >
                              Clear Filters
                            </button>
                            <button
                              type='button'
                              className='btn btn-primary'
                              onClick={() => setShowReferralFilters(false)}
                            >
                              Apply Filters
                            </button>
                          </div>
                        </div>
                      )}
                    </div>

                    <div className='col-md-6'>
                      <button
                        className='export-button w-100 btn'
                        onClick={handleExport}
                        disabled={isExporting}
                      >
                        {isExporting ? <span>Exporting...</span> : <span>Export</span>}
                      </button>
                    </div>
                  </div>
                </div>
              </div>

              <div className='table-container mt-3 border'>
                <div className='table-responsive'>
                  <table className='referrals-table'>
                    <thead className='table-header'>
                      <tr>
                        <th className='header-cell sn'>S/N</th>
                        <th className='header-cell referrer'>Referrer</th>
                        {/* <th className='header-cell code'>Referral Code</th> */}
                        <th className='header-cell invited'>Friends Invited</th>
                        <th className='header-cell status'>Status</th>
                        <th className='header-cell amount'>Reward Amount</th>
                        <th className='header-cell fueled'>Date</th>
                        <th className='header-cell action'>Action</th>
                      </tr>
                    </thead>
                    <tbody className='table-body'>
                      {isLoading ? (
                        <tr>
                          <td className='table-cell text-center' colSpan={7}>
                            <HorizontalLoader />
                          </td>
                        </tr>
                      ) : allReferrals.length === 0 ? (
                        <tr>
                          <td className='table-cell p-4 text-center' colSpan={7}>
                            No referrals found
                          </td>
                        </tr>
                      ) : (
                        allReferrals.map((ref: ReferalXModel, index: Key) => (
                          <tr key={ref.resourceId} className='table-row'>
                            <td className='table-cell sn'>
                              {Number(index) + 1 + (currentPage - 1) * referralLimit}
                            </td>
                            <td className='table-cell referrer'>
                              {ref.referrer.firstName} {ref.referrer.lastName}
                            </td>
                            {/* <td className='table-cell code'>{ref.referrer.resourceId}</td> */}
                            <td className='table-cell invited'>
                              {ref.referred.firstName} {ref.referred.lastName}
                            </td>
                            <td className='table-cell status'>
                              <span
                                className={
                                  ref.status === 'ORDER_PLACED'
                                    ? 'status-badge'
                                    : 'status-badge-pending'
                                }
                              >
                                {formatString(ref.status)}
                              </span>
                            </td>
                            <td className='table-cell amount'>
                              ₦{(ref.referrerRewardMinor / 100).toFixed(2)}
                            </td>
                            <td className='table-cell Date'>{DateTimeFormatter(ref.createdAt)}</td>
                            <td className='table-cell action'>
                              <Link
                                className='action-link'
                                to={`/boostxpress/referrals/${ref.resourceId}`}
                              >
                                View
                              </Link>
                            </td>
                          </tr>
                        ))
                      )}
                    </tbody>
                  </table>
                </div>
              </div>

              <ListPagination
                page={currentPage}
                limit={referralLimit}
                total={total}
                disabled={isLoading}
                onPageChange={setCurrentPage}
                onPageSizeChange={changeReferralPageSize}
              />
            </div>
          </div>
        ) : (
          <>
            <div className='row g-3 bx-referrals__cards-row'>
              {bnpmCards.map((card) => (
                <div key={card.label} className='col-12 col-sm-6 col-xl-3'>
                  <div
                    className={`bx-referrals__metric-card bx-referrals__metric-card--${card.tone}`}
                  >
                    <p className='bx-referrals__metric-label'>{card.label}</p>
                    <h3 className='bx-referrals__metric-value'>
                      {bnpmConfigLoading ? 'Loading...' : card.value}
                    </h3>
                  </div>
                </div>
              ))}
            </div>

            <div className='bx-referrals__toolbar row g-3 align-items-center'>
              <div className='col-lg'>
                <div className='bx-referrals__search'>
                  <span className='bx-referrals__search-icon'>
                    <svg
                      width='18'
                      height='18'
                      viewBox='0 0 24 24'
                      fill='none'
                      xmlns='http://www.w3.org/2000/svg'
                    >
                      <path
                        d='M21 21L16.65 16.65M18 10.5C18 14.6421 14.6421 18 10.5 18C6.35786 18 3 14.6421 3 10.5C3 6.35786 6.35786 3 10.5 3C14.6421 3 18 6.35786 18 10.5Z'
                        stroke='#868484'
                        strokeWidth='2'
                        strokeLinecap='round'
                        strokeLinejoin='round'
                      />
                    </svg>
                  </span>
                  <input
                    type='text'
                    className='bx-referrals__search-input'
                    placeholder='Search by User Resource ID'
                    value={bnpmSearchUserResourceId}
                    onChange={(event) => {
                      setBnpmSearchUserResourceId(event.target.value)
                      setBnpmPage(1)
                    }}
                  />
                </div>
              </div>

              <div className='col-lg-auto'>
                <div className='bx-referrals__toolbar-actions'>
                  <div className='position-relative'>
                    <button
                      type='button'
                      className='bx-referrals__action-btn'
                      onClick={() => setShowBnpmFilters((previous) => !previous)}
                    >
                      <span className='bx-referrals__action-icon'>
                        <svg
                          width='18'
                          height='18'
                          viewBox='0 0 24 24'
                          fill='none'
                          xmlns='http://www.w3.org/2000/svg'
                        >
                          <path
                            d='M4 6H20M7 12H17M10 18H14'
                            stroke='#868484'
                            strokeWidth='2'
                            strokeLinecap='round'
                            strokeLinejoin='round'
                          />
                        </svg>
                      </span>
                      <span>Filters</span>
                    </button>

                    {showBnpmFilters ? (
                      <div className='bx-referrals__filters-dropdown'>
                        <div className='bx-referrals__filters-header'>
                          <h5>Filter BNPM Wins</h5>
                          <button type='button' onClick={() => setShowBnpmFilters(false)}>
                            ×
                          </button>
                        </div>

                        <div className='bx-referrals__filters-body'>
                          <div className='bx-referrals__filter-group'>
                            <label>Sort Order</label>
                            <select
                              className='form-control'
                              value={bnpmSortOrder}
                              onChange={(event) =>
                                setBnpmSortOrder(event.target.value as SortOrder)
                              }
                            >
                              <option value='DESC'>DESC</option>
                              <option value='ASC'>ASC</option>
                            </select>
                          </div>

                          <div className='bx-referrals__filter-group'>
                            <label>User Resource ID</label>
                            <input
                              className='form-control'
                              value={bnpmSearchUserResourceId}
                              onChange={(event) => setBnpmSearchUserResourceId(event.target.value)}
                              placeholder='Enter USER ID'
                            />
                          </div>

                          <div className='bx-referrals__filter-group'>
                            <label>Start Date</label>
                            <input
                              type='date'
                              className='form-control'
                              value={bnpmStartDate}
                              onChange={(event) => setBnpmStartDate(event.target.value)}
                            />
                          </div>

                          <div className='bx-referrals__filter-group'>
                            <label>End Date</label>
                            <input
                              type='date'
                              className='form-control'
                              value={bnpmEndDate}
                              onChange={(event) => setBnpmEndDate(event.target.value)}
                            />
                          </div>
                        </div>

                        <div className='bx-referrals__filters-footer'>
                          <button
                            type='button'
                            className='btn btn-secondary'
                            onClick={clearBnpmFilters}
                          >
                            Clear Filters
                          </button>
                          <button
                            type='button'
                            className='btn btn-primary'
                            onClick={() => setShowBnpmFilters(false)}
                          >
                            Apply Filters
                          </button>
                        </div>
                      </div>
                    ) : null}
                  </div>

                  <button type='button' className='bx-referrals__action-btn'>
                    <span className='bx-referrals__action-icon'>
                      <svg
                        width='18'
                        height='18'
                        viewBox='0 0 24 24'
                        fill='none'
                        xmlns='http://www.w3.org/2000/svg'
                      >
                        <path
                          d='M14 3V7C14 7.53043 14.2107 8.03914 14.5858 8.41421C14.9609 8.78929 15.4696 9 16 9H20M12 11V17M12 17L9.5 14.5M12 17L14.5 14.5M8 21H16C17.1046 21 18 20.1046 18 19V9.5L13.5 5H8C6.89543 5 6 5.89543 6 7V19C6 20.1046 6.89543 21 8 21Z'
                          stroke='#868484'
                          strokeWidth='1.8'
                          strokeLinecap='round'
                          strokeLinejoin='round'
                        />
                      </svg>
                    </span>
                    <span>Export</span>
                  </button>
                </div>
              </div>
            </div>

            <div className='bx-referrals__table-shell'>
              <div className='table-responsive'>
                <table className='bx-referrals__table'>
                  <thead>
                    <tr>
                      <th>S/N</th>
                      <th>Customer Name</th>
                      <th>Order Ref</th>
                      <th>Win Type</th>
                      <th>Customer Email</th>
                      <th>Amount won</th>
                      <th>Date</th>
                      <th>Action</th>
                    </tr>
                  </thead>
                  <tbody>{renderBnpmTableBody()}</tbody>
                </table>
              </div>

              <ListPagination
                page={bnpmPage}
                limit={bnpmLimit}
                total={bnpmWinsTotal}
                disabled={bnpmWinsLoading}
                onPageChange={setBnpmPage}
                onPageSizeChange={changeBnpmPageSize}
              />
            </div>
          </>
        )}
      </div>

      <ModalWrapper
        showModal={isReferralConfigModalOpen}
        closeModal={() => setIsReferralConfigModalOpen(false)}
        title='Referral Configuration'
        size='lg'
        hideHeader={false}
      >
        <form onSubmit={handleSubmit(handleReferralConfigSubmit)}>
          <div className='referral-config-form'>
            <div className='form-group'>
              <label className='form-label'>Referrer Bonus Percentage (%)</label>
              <input
                type='number'
                className='form-input'
                step='0.01'
                min='0'
                max='100'
                {...register('referrerBonusPercentage', {
                  required: true,
                  min: 0,
                  max: 100,
                  valueAsNumber: true,
                })}
              />
            </div>

            <div className='form-group'>
              <label className='form-label'>Maximum Bonus Value</label>
              <input
                type='number'
                className='form-input'
                step='0.01'
                {...register('maxBonusMajor', { required: true, valueAsNumber: true })}
              />
            </div>

            <div className='form-group'>
              <label className='form-label'>Welcome Bonus Percentage (%)</label>
              <input
                type='number'
                className='form-input'
                step='0.01'
                min='0'
                max='100'
                {...register('welcomeBonusPercentage', {
                  required: true,
                  min: 0,
                  max: 100,
                  valueAsNumber: true,
                })}
              />
            </div>

            <div className='form-group'>
              <label className='form-label'>Enable Referral System</label>
              <div className='checkbox-group'>
                <label className='checkbox-label'>
                  <input type='checkbox' {...register('isActive')} />
                  <span className='checkbox-text'>Active</span>
                </label>
              </div>
            </div>

            <div className='form-actions'>
              <button
                type='submit'
                className='btn btn-primary'
                disabled={isReferralConfigSubmitting}
              >
                {isReferralConfigSubmitting ? 'Saving...' : 'Save Configuration'}
              </button>
              <button
                type='button'
                className='btn btn-secondary'
                onClick={() => setIsReferralConfigModalOpen(false)}
              >
                Cancel
              </button>
            </div>
          </div>
        </form>
      </ModalWrapper>

      <ModalWrapper
        showModal={isConfigModalOpen}
        closeModal={closeConfigModal}
        title='BNPM Configuration'
        size='lg'
        hideHeader={true}
      >
        <form
          className='bx-bnpm-modal'
          onSubmit={(event) => void handleSubmitBnpm(handleConfigSubmit)(event)}
        >
          <div className='bx-bnpm-modal__header'>
            <h3>BNPM Configuration</h3>
            <button type='button' className='bx-bnpm-modal__close' onClick={closeConfigModal}>
              ×
            </button>
          </div>

          <div className='bx-bnpm-modal__divider' />

          <div className='bx-bnpm-modal__fields'>
            <div className='bx-bnpm-modal__field'>
              <label>Win Probability</label>
              <input
                type='number'
                step='0.01'
                min='0'
                max='100'
                {...registerBnpm('winProbability', {
                  required: 'Win probability is required',
                  setValueAs: (value) => (value === '' ? undefined : Number(value)),
                  min: { value: 0, message: 'Win probability cannot be less than 0' },
                  max: { value: 100, message: 'Win probability cannot be greater than 100' },
                })}
              />
              {bnpmErrors.winProbability ? (
                <p className='bx-bnpm-modal__error'>{bnpmErrors.winProbability.message}</p>
              ) : null}
            </div>

            <div className='bx-bnpm-modal__field'>
              <label>Win Percentage</label>
              <input
                type='number'
                step='0.01'
                min='0'
                max='100'
                {...registerBnpm('winPercentage', {
                  required: 'Win percentage is required',
                  setValueAs: (value) => (value === '' ? undefined : Number(value)),
                  min: { value: 0, message: 'Win percentage cannot be less than 0' },
                  max: { value: 100, message: 'Win percentage cannot be greater than 100' },
                })}
              />
              {bnpmErrors.winPercentage ? (
                <p className='bx-bnpm-modal__error'>{bnpmErrors.winPercentage.message}</p>
              ) : null}
            </div>

            <div className='bx-bnpm-modal__field'>
              <label>First Win Cap (Major)</label>
              <input
                type='number'
                min='0'
                step='1'
                {...registerBnpm('firstWinCapMajor', {
                  required: 'First win cap is required',
                  setValueAs: (value) => (value === '' ? undefined : Number(value)),
                  min: { value: 0, message: 'First win cap cannot be less than 0' },
                })}
              />
              {bnpmErrors.firstWinCapMajor ? (
                <p className='bx-bnpm-modal__error'>{bnpmErrors.firstWinCapMajor.message}</p>
              ) : null}
            </div>

            <div className='bx-bnpm-modal__field'>
              <label>Subsequent Win Cap (Major)</label>
              <input
                type='number'
                min='0'
                step='1'
                {...registerBnpm('subsequentWinCapMajor', {
                  required: 'Subsequent win cap is required',
                  setValueAs: (value) => (value === '' ? undefined : Number(value)),
                  min: { value: 0, message: 'Subsequent win cap cannot be less than 0' },
                })}
              />
              {bnpmErrors.subsequentWinCapMajor ? (
                <p className='bx-bnpm-modal__error'>{bnpmErrors.subsequentWinCapMajor.message}</p>
              ) : null}
            </div>

            <div className='bx-bnpm-modal__field'>
              <label>Max Wins In Window</label>
              <input
                type='number'
                min='0'
                step='1'
                {...registerBnpm('maxWinsInWindow', {
                  required: 'Max wins in window is required',
                  setValueAs: (value) => (value === '' ? undefined : Number(value)),
                  min: { value: 0, message: 'Max wins in window cannot be less than 0' },
                })}
              />
              {bnpmErrors.maxWinsInWindow ? (
                <p className='bx-bnpm-modal__error'>{bnpmErrors.maxWinsInWindow.message}</p>
              ) : null}
            </div>

            <div className='bx-bnpm-modal__field'>
              <label>Window Size</label>
              <input
                type='number'
                min='0'
                step='1'
                {...registerBnpm('windowSize', {
                  required: 'Window size is required',
                  setValueAs: (value) => (value === '' ? undefined : Number(value)),
                  min: { value: 0, message: 'Window size cannot be less than 0' },
                })}
              />
              {bnpmErrors.windowSize ? (
                <p className='bx-bnpm-modal__error'>{bnpmErrors.windowSize.message}</p>
              ) : null}
            </div>

            <div className='bx-bnpm-modal__field'>
              <label>BNPM Campaign Budget</label>
              <input
                type='number'
                min='0'
                step='1'
                {...registerBnpm('campaignBudgetMajor', {
                  required: 'Campaign budget is required',
                  setValueAs: (value) => (value === '' ? undefined : Number(value)),
                  min: { value: 0, message: 'Campaign budget cannot be less than 0' },
                })}
              />
              {bnpmErrors.campaignBudgetMajor ? (
                <p className='bx-bnpm-modal__error'>{bnpmErrors.campaignBudgetMajor.message}</p>
              ) : null}
            </div>

            <div className='bx-bnpm-modal__field'>
              <label>Referrer Cut Percentage</label>
              <input
                type='number'
                step='0.01'
                min='0'
                max='100'
                {...registerBnpm('referrerCutPercentage', {
                  required: 'Referrer cut percentage is required',
                  setValueAs: (value) => (value === '' ? undefined : Number(value)),
                  min: { value: 0, message: 'Referrer cut percentage cannot be less than 0' },
                  max: {
                    value: 100,
                    message: 'Referrer cut percentage cannot be greater than 100',
                  },
                })}
              />
              {bnpmErrors.referrerCutPercentage ? (
                <p className='bx-bnpm-modal__error'>{bnpmErrors.referrerCutPercentage.message}</p>
              ) : null}
            </div>

            <div className='bx-bnpm-modal__field'>
              <label>Ends At</label>
              <input
                type='datetime-local'
                {...registerBnpm('endsAt', {
                  required: 'End date is required',
                })}
              />
              {bnpmErrors.endsAt ? (
                <p className='bx-bnpm-modal__error'>{bnpmErrors.endsAt.message}</p>
              ) : null}
            </div>

            <div className='bx-bnpm-modal__toggle-row'>
              <label>Toggle on to activate BNPM</label>
              <input type='hidden' {...registerBnpm('isActive')} />
              <button
                type='button'
                aria-pressed={bnpmIsActive}
                className={`bx-bnpm-modal__toggle ${bnpmIsActive ? 'bx-bnpm-modal__toggle--active' : ''}`}
                onClick={() =>
                  setBnpmValue('isActive', !bnpmIsActive, {
                    shouldDirty: true,
                    shouldValidate: true,
                  })
                }
              >
                <span />
              </button>
            </div>
          </div>

          <div className='bx-bnpm-modal__footer'>
            <p className='bx-bnpm-modal__updated-at'>
              Last updated{' '}
              {bnpmConfig.updatedAt ? TimeConverter(bnpmConfig.updatedAt) : 'Not available'}
            </p>

            <div className='bx-bnpm-modal__actions'>
              <button type='button' className='bx-bnpm-modal__secondary' onClick={closeConfigModal}>
                Cancel
              </button>
              <button
                type='submit'
                className='bx-bnpm-modal__primary'
                disabled={isConfigSubmitting}
              >
                {isConfigSubmitting ? 'Saving...' : 'Save Configuration'}
              </button>
            </div>
          </div>
        </form>
      </ModalWrapper>
      <ToastContainer></ToastContainer>
    </div>
  )
}

export default Referrals
